// NavGroup.jsx
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { matchPath, useLocation } from 'react-router-dom';
import { Divider, List, ListItemButton, ListItemText, Collapse, Typography } from '@mui/material';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import NavCollapse from '../NavCollapse';
import NavItem from '../NavItem';
import { useGetMenuMaster } from '../../../../api/menu';

export default function NavGroup({ item, lastItem, remItems, lastItemId, setSelectedID }) {
  const { pathname } = useLocation();
  const { menuMaster } = useGetMenuMaster();
  const drawerOpen = menuMaster.isDashboardDrawerOpened;
  const [currentItem, setCurrentItem] = useState(item);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (lastItem && item.id === lastItemId) {
      const localItem = { ...item };
      const elements = remItems.map((ele) => ele.elements);
      localItem.children = elements.flat(1);
      setCurrentItem(localItem);
    } else {
      setCurrentItem(item);
    }
  }, [item, lastItem, remItems, lastItemId]);

  useEffect(() => {
    const checkSelected = (data) => {
      const children = data.children || [];
      children.forEach((c) => {
        if (c?.url && matchPath({ path: c.link || c.url, end: true }, pathname)) {
          setSelectedID(currentItem.id);
          if (children.length > 1) setOpen(true);
        }
      });
    };
    checkSelected(currentItem);
  }, [pathname, currentItem, setSelectedID]);

  const items = currentItem.children?.map((menu, idx) => {
    const delay = drawerOpen ? `${idx * 30}ms` : '0ms';
    switch (menu?.type) {
      case 'collapse':
        return (
          <div key={menu.id} style={{ animation: `fadeIn 0.25s ease-out ${delay} both` }}>
            <NavCollapse menu={menu} level={1} />
          </div>
        );
      case 'item':
        return (
          <div key={menu.id} style={{ animation: `fadeIn 0.25s ease-out ${delay} both` }}>
            <NavItem item={menu} level={1} />
          </div>
        );
      default:
        return (
          <Typography key={menu?.id} variant="h6" color="error" align="center">
            Menu Items Error
          </Typography>
        );
    }
  }) || [];

  const hasMultipleChildren = currentItem.children?.length > 1;

  return (
    <List disablePadding sx={{ mb: 1.0 }}>
      {drawerOpen && currentItem.title && (
        <>
          {hasMultipleChildren ? (
            <>
              <ListItemButton
                onClick={() => setOpen((prev) => !prev)}
                sx={{
                  px: '12px',
                  py: '4px',
                  borderRadius: '5px',
                  color: open ? '#3498DB !important' : '#2C3E50 !important',
                  backgroundColor: open ? 'rgba(52, 152, 219, 0.08)' : 'transparent',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    backgroundColor: 'rgba(52, 152, 219, 0.12) !important'
                  }
                }}
              >
                <ListItemText
                  primary={
                    <Typography
                      variant="body2"
                      sx={{
                        // Only first letter capitalized (natural casing)
                        letterSpacing: '0.4px',
                        fontWeight: 700,
                        color: 'inherit !important',
                        fontSize: '0.85rem',
                        fontFamily: 'inherit'
                      }}
                    >
                      {currentItem.title}
                    </Typography>
                  }
                />
                {open ? (
                  <ExpandLess sx={{ color: '#3498DB !important', fontSize: '16px' }} />
                ) : (
                  <ExpandMore sx={{ color: '#95A5A6 !important', fontSize: '16px' }} />
                )}
              </ListItemButton>

              <Collapse in={open} timeout="auto" unmountOnExit>
                <List
                  disablePadding
                  sx={{
                    position: 'relative',
                    pl: '12px',
                    '&:before': {
                      content: '""',
                      position: 'absolute',
                      left: '6px',
                      top: 0,
                      bottom: 0,
                      width: '2px',
                      background: 'linear-gradient(to bottom, #3498DB, #2980B9)'
                    }
                  }}
                >
                  {items}
                </List>
              </Collapse>
            </>
          ) : (
            <>
              <Typography
                variant="body2"
                sx={{
                  px: '12px',
                  pt: '7px',
                  pb: '3px',
                  // No uppercase — natural title casing
                  letterSpacing: '0.4px',
                  fontWeight: 700,
                  color: '#7F8C8D !important',
                  fontSize: '0.9rem',
                  fontFamily: 'inherit'
                }}
              >
                {currentItem.title}
              </Typography>
              {items}
            </>
          )}
        </>
      )}

      {!drawerOpen && items}

      {drawerOpen && (
        <Divider
          sx={{
            my: 1.0,
            height: '1px',
            backgroundColor: '#DDD'
          }}
        />
      )}

      <style jsx global>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateX(-6px); }
          to { opacity: 1; transform: translateX(0); }
        }
      `}</style>
    </List>
  );
}

NavGroup.propTypes = {
  item: PropTypes.any,
  lastItem: PropTypes.number,
  remItems: PropTypes.array,
  lastItemId: PropTypes.string,
  selectedID: PropTypes.oneOfType([PropTypes.any, PropTypes.string]),
  setSelectedID: PropTypes.func
};